// Модальні шари: Dialog, SidePanel (бічна панель), ConfirmDialog, confirm() + ConfirmHost.
// Портал у body, фокус-пастка, Esc, блокування прокрутки, повернення фокусу, aria-modal.
import { AlertTriangle, X } from 'lucide-react';
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { Button } from './Button';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.getAttribute('aria-hidden') !== 'true' && el.offsetParent !== null,
  );
}

export function Portal({ children }: { children: ReactNode }) {
  return createPortal(children, document.body);
}

let scrollLocks = 0;

/** Блокує прокрутку body, поки active. */
export function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    scrollLocks++;
    if (scrollLocks === 1) {
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;
    }
    return () => {
      scrollLocks--;
      if (scrollLocks === 0) {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    };
  }, [active]);
}

/** Фокус-пастка: фокус на initialFocus / [data-autofocus] / перший елемент; Tab — по колу; при закритті фокус повертається. */
export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean, initialFocus?: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;
    const previous = document.activeElement as HTMLElement | null;
    const target =
      initialFocus?.current ??
      container.querySelector<HTMLElement>('[data-autofocus]') ??
      focusables(container).find((el) => !el.dataset.closeButton) ??
      container;
    target.focus({ preventScroll: true });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables(container);
      if (!items.length) {
        e.preventDefault();
        container.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const activeEl = document.activeElement;
      if (e.shiftKey && (activeEl === first || activeEl === container)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      if (previous && document.contains(previous)) previous.focus({ preventScroll: true });
    };
  }, [active, containerRef, initialFocus]);
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      data-close-button="true"
      onClick={onClick}
      className="-mt-1 -mr-1 flex size-8 shrink-0 items-center justify-center rounded-field text-muted transition-colors hover:bg-surface-2 hover:text-fg"
      aria-label="Закрити"
    >
      <X className="size-[18px]" />
    </button>
  );
}

// ---------------------------------------------------------------- Dialog

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Кнопки внизу (праворуч). */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Закривати кліком по фону (за замовчуванням true). */
  closeOnOverlay?: boolean;
  /** false — заборонити закриття (напр. поки йде збереження). */
  dismissable?: boolean;
  initialFocus?: RefObject<HTMLElement | null>;
  className?: string;
  bodyClassName?: string;
}

const dialogSizes = { sm: 'sm:max-w-[440px]', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' };

/** Модальне вікно. Форма: <form id="x"> у children + <Button type="submit" form="x"> у footer. */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnOverlay = true,
  dismissable = true,
  initialFocus,
  className,
  bodyClassName,
}: DialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  useScrollLock(open);
  useFocusTrap(ref, open, initialFocus);
  if (!open) return null;
  const close = () => dismissable && onClose();
  return (
    <Portal>
      <div
        className="fixed inset-0 z-50 flex items-end justify-center bg-overlay p-0 animate-fade-in sm:items-center sm:p-4"
        onMouseDown={(e) => {
          if (closeOnOverlay && e.target === e.currentTarget) close();
        }}
      >
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descId : undefined}
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              close();
            }
          }}
          className={cn(
            'flex max-h-[92dvh] w-full flex-col rounded-t-card bg-surface shadow-dialog animate-slide-in-up sm:animate-pop-in sm:rounded-card',
            dialogSizes[size],
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3">
            <div className="min-w-0">
              <h2 id={titleId} className="text-[18px] leading-6 font-semibold">
                {title}
              </h2>
              {description ? (
                <p id={descId} className="mt-1 text-[13px] text-muted">
                  {description}
                </p>
              ) : null}
            </div>
            {dismissable ? <CloseButton onClick={close} /> : null}
          </div>
          <div className={cn('min-h-0 flex-1 overflow-y-auto px-5 py-2 scroll-thin', bodyClassName)}>{children}</div>
          {footer ? (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-divider px-5 py-3 max-sm:pb-[max(12px,env(safe-area-inset-bottom))]">
              {footer}
            </div>
          ) : (
            <div className="h-3 shrink-0" aria-hidden />
          )}
        </div>
      </div>
    </Portal>
  );
}

// ---------------------------------------------------------------- SidePanel

export interface SidePanelProps extends Omit<DialogProps, 'size'> {
  /** Ширина панелі на десктопі. */
  width?: 'sm' | 'md' | 'lg' | 'xl';
}

const panelWidths = { sm: 'sm:max-w-sm', md: 'sm:max-w-md', lg: 'sm:max-w-xl', xl: 'sm:max-w-3xl' };

/** Бічна панель праворуч — перегляд/редагування запису без переходу зі сторінки. На мобільному — на весь екран. */
export function SidePanel({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 'md',
  closeOnOverlay = true,
  dismissable = true,
  initialFocus,
  className,
  bodyClassName,
}: SidePanelProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  useScrollLock(open);
  useFocusTrap(ref, open, initialFocus);
  if (!open) return null;
  const close = () => dismissable && onClose();
  return (
    <Portal>
      <div
        className="fixed inset-0 z-50 flex justify-end bg-overlay animate-fade-in"
        onMouseDown={(e) => {
          if (closeOnOverlay && e.target === e.currentTarget) close();
        }}
      >
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descId : undefined}
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              close();
            }
          }}
          className={cn('flex h-full w-full flex-col bg-surface shadow-dialog animate-slide-in-right sm:rounded-l-card', panelWidths[width], className)}
        >
          <div className="flex items-start justify-between gap-4 border-b border-divider px-5 pt-5 pb-4">
            <div className="min-w-0">
              <h2 id={titleId} className="truncate text-[18px] leading-6 font-semibold">
                {title}
              </h2>
              {description ? (
                <p id={descId} className="mt-1 text-[13px] text-muted">
                  {description}
                </p>
              ) : null}
            </div>
            {dismissable ? <CloseButton onClick={close} /> : null}
          </div>
          <div className={cn('min-h-0 flex-1 overflow-y-auto px-5 py-4 scroll-thin', bodyClassName)}>{children}</div>
          {footer ? (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-divider px-5 py-3 pb-[max(12px,env(safe-area-inset-bottom))]">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </Portal>
  );
}

// ---------------------------------------------------------------- ConfirmDialog

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Незворотна дія (видалення) — червона кнопка й іконка попередження. */
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}

/** Підтвердження дії (CONVENTIONS §3: підтвердження незворотних дій). */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Підтвердити',
  cancelLabel = 'Скасувати',
  danger,
  loading,
  onConfirm,
  onCancel,
  children,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      size="sm"
      dismissable={!loading}
      initialFocus={danger ? cancelRef : undefined}
      title={
        <span className="flex items-center gap-2.5">
          {danger ? (
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-danger-soft text-danger" aria-hidden>
              <AlertTriangle className="size-4" />
            </span>
          ) : null}
          {title}
        </span>
      }
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading} data-autofocus={danger ? undefined : true}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {description ? <p className="text-[14px] text-fg-2">{description}</p> : null}
      {children}
    </Dialog>
  );
}

// ---------------------------------------------------------------- confirm() — імперативне підтвердження

interface ConfirmRequest {
  id: number;
  options: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

let pending: ConfirmRequest | null = null;
let confirmSeq = 0;
const confirmListeners = new Set<() => void>();

/** `if (await confirm({ title: 'Видалити запис?', danger: true })) …` — потребує <ConfirmHost/> у дереві. */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    pending?.resolve(false);
    pending = { id: ++confirmSeq, options, resolve };
    for (const l of confirmListeners) l();
  });
}

function settle(ok: boolean) {
  const p = pending;
  pending = null;
  p?.resolve(ok);
  for (const l of confirmListeners) l();
}

/** Хост імперативних підтверджень (змонтований один раз у main.tsx). */
export function ConfirmHost() {
  const req = useSyncExternalStore(
    (cb) => {
      confirmListeners.add(cb);
      return () => confirmListeners.delete(cb);
    },
    () => pending,
    () => null,
  );
  const shown = req;
  if (!shown) return null;
  return (
    <ConfirmDialog
      open
      title={shown.options.title}
      description={shown.options.description}
      confirmLabel={shown.options.confirmLabel}
      cancelLabel={shown.options.cancelLabel}
      danger={shown.options.danger}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );
}
